import { 
    Box
} from "@mui/material";
import React, { useState } from "react";
import { PackageHistoryDto, RestorePackageVersionCommand } from "../../api/models/PackageDto";
import { restorePackageVersion } from "../../api/adminPackageClient";
import { checkForDifferences, getSelectedVersionsData } from "./utils/comparisonUtils";
import ComparisonControls from "./components/ComparisonControls";
import HistoryTable from "./components/HistoryTable";
import PackageComparisonDialog from "./components/PackageComparisonDialog";
import RestoreConfirmationDialog from "./components/RestoreConfirmationDialog";

interface PackageHistoryGridProps {
    packageId: string;
    history: PackageHistoryDto[];
    reloadData: () => Promise<void>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const PackageHistoryGrid = ({ packageId, history, reloadData, setIsLoading }: PackageHistoryGridProps): JSX.Element => {
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [showComparison, setShowComparison] = useState(false);
    const [hasChanges, setHasChanges] = useState(true);
    const [restoreDialog, setRestoreDialog] = useState<{ open: boolean; version: PackageHistoryDto | null }>({
        open: false,
        version: null
    });

    const handleVersionSelect = (validFrom: string, checked: boolean) => {
        if (checked) {
            if (selectedVersions.length < 2) {
                setSelectedVersions(prev => [...prev, validFrom]);
            }
        } else {
            setSelectedVersions(prev => prev.filter(v => v !== validFrom));
        }
    };

    const handleCompare = () => {
        if (selectedVersions.length === 2) {
            const versions = getSelectedVersionsData(selectedVersions, history);
            const differences = checkForDifferences(versions);
            setHasChanges(differences);
            setShowComparison(true);
        }
    };

    const handleCloseComparison = () => {
        setShowComparison(false);
    };

    const handleRestore = (version: PackageHistoryDto) => {
        setRestoreDialog({ open: true, version });
    };

    const handleCloseRestoreDialog = () => {
        setRestoreDialog({ open: false, version: null });
    };

    const confirmRestore = async () => {
        if (restoreDialog.version) {
            setIsLoading(true);
            try {
                const command: RestorePackageVersionCommand = {
                    packageId,
                    validFrom: restoreDialog.version.validFrom
                };
                await restorePackageVersion(command);
                await reloadData();
            } catch (error) {
                console.error("Failed to restore package version:", error);
            } finally {
                setIsLoading(false);
                setRestoreDialog({ open: false, version: null });
            }
        }
    };

    return (
        <div data-testid="package-history-grid">
            <Box pl="80px" pt="24px" height="100%">
                <ComparisonControls 
                    selectedVersionsCount={selectedVersions.length}
                    onCompare={handleCompare}
                />

                <HistoryTable 
                    history={history}
                    selectedVersions={selectedVersions}
                    onVersionSelect={handleVersionSelect}
                    onRestore={handleRestore}
                />

                <PackageComparisonDialog 
                    open={showComparison}
                    onClose={handleCloseComparison}
                    selectedVersions={selectedVersions}
                    history={history}
                    hasChanges={hasChanges}
                />

                <RestoreConfirmationDialog 
                    open={restoreDialog.open}
                    version={restoreDialog.version}
                    onClose={handleCloseRestoreDialog}
                    onConfirm={confirmRestore}
                />
            </Box>
        </div>
    );
};

export default PackageHistoryGrid;